import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import Preloader from '../../components/Preloader';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const DoctorProgressReport = () => {
  const { user } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadProgressReport();
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(loadProgressReport, 30000);
      
      // Listen for real-time question answered events
      const handleQuestionAnswered = (event) => {
        const { answeredBy } = event.detail;
        // Only refresh if this doctor answered the question
        if (answeredBy === user.id) {
          console.log('Question answered by current doctor, refreshing progress report...');
          loadProgressReport();
        }
      };
      
      window.addEventListener('questionAnswered', handleQuestionAnswered);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('questionAnswered', handleQuestionAnswered);
      };
    }
  }, [user]);

  const loadProgressReport = async () => {
    try {
      setError('');
      const response = await api.get('/doctors/progress-report');
      setReportData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading progress report:', err);
      setError(err.response?.data?.message || 'Failed to load progress report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!reportData || isGeneratingPDF) return;

    setIsGeneratingPDF(true);
    
    try {
      // Create a temporary container for PDF generation
      const element = reportRef.current;
      if (!element) {
        throw new Error('Report element not found');
      }

      // Configure html2canvas options for better quality
      const canvas = await html2canvas(element, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position = 0;

      // Add the image to PDF
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `progress-report-${user.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const generateReportContent = () => {
    if (!reportData) return '';

    const { doctor_name, doctor_email, overall_stats, protocols, work_history, completed_assignments, inactive_questions_summary, batches, batch_totals } = reportData;
    const reportDate = new Date().toLocaleString();

    let content = `DOCTOR PROGRESS REPORT\n`;
    content += `${'='.repeat(50)}\n\n`;
    content += `Doctor: ${doctor_name}\n`;
    content += `Email: ${doctor_email}\n`;
    content += `Report Generated: ${reportDate}\n\n`;

    content += `OVERALL STATISTICS\n`;
    content += `${'-'.repeat(30)}\n`;
    content += `Total Protocols: ${overall_stats.total_protocols}\n`;
    content += `Total Cases: ${overall_stats.total_cases}\n`;
    content += `Total Questions Available: ${overall_stats.total_questions_available}\n`;
    content += `Total Questions Answered: ${overall_stats.total_questions_answered}\n`;
    content += `Total Inactive Questions Assigned: ${overall_stats.total_inactive_questions_assigned || 0}\n`;
    content += `Overall Completion: ${overall_stats.overall_completion_percentage}%\n\n`;

    if (batch_totals && batch_totals.length > 0) {
      content += `BATCH TOTALS (All Active Batches)\n`;
      content += `${'-'.repeat(30)}\n`;
      content += `Total Batches: ${batch_totals.length}\n\n`;
      
      batch_totals.forEach((batch, index) => {
        content += `${index + 1}. ${batch.batch_name}\n`;
        content += `   Total Cases: ${batch.total_cases_in_batch}\n`;
        content += `   Total Questions: ${batch.total_questions_in_batch}\n`;
        content += `\n`;
      });
    }

    content += `CURRENT PROTOCOL ASSIGNMENTS\n`;
    content += `${'-'.repeat(30)}\n`;

    protocols.forEach((protocol, index) => {
      content += `${index + 1}. ${protocol.protocol_name}\n`;
      content += `   Description: ${protocol.protocol_description || 'N/A'}\n`;
      content += `   Total Cases: ${protocol.total_cases}\n`;
      content += `   Questions per Case: ${protocol.total_questions}\n`;
      content += `   Questions Answered: ${protocol.answered_questions}\n`;
      content += `   Completion: ${protocol.completion_percentage}%\n`;
      content += `   Cases Completed: ${protocol.cases_completed}\n`;
      content += `   Cases In Progress: ${protocol.cases_in_progress}\n`;
      content += `   Cases Not Started: ${protocol.total_cases - protocol.cases_completed - protocol.cases_in_progress}\n\n`;
    });

    if (completed_assignments && completed_assignments.length > 0) {
      content += `COMPLETED ASSIGNMENTS\n`;
      content += `${'-'.repeat(30)}\n`;
      completed_assignments.forEach((assignment, index) => {
        content += `${index + 1}. ${assignment.protocol_name} - Case ${assignment.case_id}\n`;
        content += `   Questions Answered: ${assignment.questions_answered}\n`;
        content += `   Completed: ${new Date(assignment.completed_at).toLocaleDateString()}\n`;
        content += `   Assigned: ${new Date(assignment.assigned_at).toLocaleDateString()}\n\n`;
      });
    }

    if (inactive_questions_summary && inactive_questions_summary.length > 0) {
      content += `INACTIVE QUESTIONS ASSIGNED TO YOU\n`;
      content += `${'-'.repeat(30)}\n`;
      const totalInactive = inactive_questions_summary.reduce((total, protocol) => total + protocol.inactive_questions_count, 0);
      content += `Total Inactive Questions: ${totalInactive}\n\n`;

      inactive_questions_summary.forEach((protocol) => {
        content += `Protocol: ${protocol.protocol_name}\n`;
        content += `Inactive Questions: ${protocol.inactive_questions_count}\n`;
        protocol.inactive_questions.slice(0, 5).forEach((question, idx) => {
          content += `  ${idx + 1}. ${question.text.substring(0, 80)}${question.text.length > 80 ? '...' : ''}\n`;
          content += `     Type: ${question.type}, Category: ${question.category || 'N/A'}\n`;
        });
        if (protocol.inactive_questions.length > 5) {
          content += `  ... and ${protocol.inactive_questions.length - 5} more questions\n`;
        }
        content += `\n`;
      });
    }

    if (work_history && work_history.length > 0) {
      content += `RECENT WORK HISTORY (Last 30 Days)\n`;
      content += `${'-'.repeat(30)}\n`;
      work_history.forEach((day) => {
        content += `${day.date}: ${day.questions_answered} questions, ${day.protocols_worked_on} protocols, ${day.cases_worked_on} cases\n`;
      });
    }

    return content;
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    if (percentage >= 40) return 'info';
    return 'danger';
  };

  const getCompletionIcon = (percentage) => {
    if (percentage >= 100) return 'fas fa-check-circle';
    if (percentage >= 80) return 'fas fa-thumbs-up';
    if (percentage >= 60) return 'fas fa-clock';
    if (percentage >= 40) return 'fas fa-hourglass-half';
    return 'fas fa-exclamation-triangle';
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Progress Report">
        <Preloader message="Loading your progress report..." />
      </ResearchLayout>
    );
  }

  if (error) {
    return (
      <ResearchLayout pageTitle="Progress Report">
        <div className="container-fluid">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button className="btn btn-outline-danger btn-sm ms-3" onClick={loadProgressReport}>
              Try Again
            </button>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (!reportData) {
    return (
      <ResearchLayout pageTitle="Progress Report">
        <div className="container-fluid">
          <div className="text-center py-5">
            <i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Progress Data Available</h5>
            <p className="text-muted">You haven't been assigned any protocols yet.</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

    const { doctor_name, doctor_email, overall_stats, protocols, work_history, completed_assignments, inactive_questions_summary, batches, batch_totals } = reportData;

  return (
    <ResearchLayout pageTitle="Progress Report">
      <div className="container-fluid" ref={reportRef}>
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm bg-gradient-primary text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">Progress Report</h3>
                    <h5 className="mb-0 opacity-75">{doctor_name}</h5>
                    <small className="opacity-75">{doctor_email}</small>
                  </div>
                  <div className="text-end">
                    <button 
                      className="btn btn-light btn-sm me-2"
                      onClick={loadProgressReport}
                      disabled={loading}
                    >
                      <i className="fas fa-sync-alt me-1"></i>
                      Refresh
                    </button>
                    <button
                      className="btn btn-outline-light btn-sm"
                      onClick={downloadReport}
                      disabled={isGeneratingPDF}
                    >
                      <i className={`fas ${isGeneratingPDF ? 'fa-spinner fa-spin' : 'fa-file-pdf'} me-1`}></i>
                      {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF'}
                    </button>
                  </div>
                </div>
                {lastUpdated && (
                  <div className="mt-2">
                    <small className="opacity-75">
                      <i className="fas fa-clock me-1"></i>
                      Last updated: {lastUpdated.toLocaleString()}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Overall Statistics */}
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-primary mb-2">
                  <i className="fas fa-clipboard-list fa-2x"></i>
                </div>
                <h4 className="fw-bold text-primary">{overall_stats.total_protocols}</h4>
                <p className="text-muted mb-0">Total Protocols</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-success mb-2">
                  <i className="fas fa-users fa-2x"></i>
                </div>
                <h4 className="fw-bold text-success">{overall_stats.total_cases}</h4>
                <p className="text-muted mb-0">Total Cases</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-warning mb-2">
                  <i className="fas fa-tasks fa-2x"></i>
                </div>
                <h4 className="fw-bold text-warning">
                  {overall_stats.total_cases_answered || 0}/{overall_stats.total_cases}
                </h4>
                <p className="text-muted mb-0">Cases Answered</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-info mb-2">
                  <i className="fas fa-question-circle fa-2x"></i>
                </div>
                <h4 className="fw-bold text-info">
                  {overall_stats.total_questions_answered}/{overall_stats.total_questions_available}
                </h4>
                <p className="text-muted mb-0">Questions Answered</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-secondary mb-2">
                  <i className="fas fa-clock fa-2x"></i>
                </div>
                <h4 className="fw-bold text-secondary">
                  {overall_stats.total_time_spent_minutes || 0} min
                </h4>
                <p className="text-muted mb-0">Time Spent</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-warning mb-2">
                  <i className="fas fa-pause-circle fa-2x"></i>
                </div>
                <h4 className="fw-bold text-warning">
                  {overall_stats.total_inactive_questions_assigned || 0}
                </h4>
                <p className="text-muted mb-0">Inactive Questions</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <div className={`text-${getCompletionColor(overall_stats.overall_completion_percentage)} mb-2`}>
                  <i className={`${getCompletionIcon(overall_stats.overall_completion_percentage)} fa-2x`}></i>
                </div>
                <h4 className={`fw-bold text-${getCompletionColor(overall_stats.overall_completion_percentage)}`}>
                  {overall_stats.overall_completion_percentage}%
                </h4>
                <p className="text-muted mb-0">Overall Completion</p>
              </div>
            </div>
          </div>
        </div>

        {/* Batch Information Section */}
        {batches && batches.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-layer-group text-primary me-2"></i>
                    Batch Assignments ({batches.length})
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {batches.map((batch) => (
                      <div key={batch.batch_id} className="col-lg-6 col-md-12 mb-4">
                        <div className="card border h-100">
                          <div className="card-header bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 fw-bold">{batch.batch_name}</h6>
                              <div className="d-flex gap-2">
                                <span className={`badge ${
                                  batch.status === 'active' ? 'bg-success' :
                                  batch.status === 'expired' ? 'bg-danger' :
                                  batch.status === 'future' ? 'bg-info' :
                                  'bg-secondary'
                                }`}>
                                  {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                                </span>
                                {batch.time_remaining && (
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-clock me-1"></i>
                                    {batch.time_remaining}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="card-body">
                            {batch.batch_description && (
                              <p className="text-muted small mb-3">{batch.batch_description}</p>
                            )}
                            
                            <div className="row mb-3">
                              <div className="col-6">
                                <div className="text-center">
                                  <div className="h5 text-primary mb-1">{batch.total_cases}</div>
                                  <div className="small text-muted">Total Cases</div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="text-center">
                                  <div className="h5 text-info mb-1">{batch.total_protocols}</div>
                                  <div className="small text-muted">Protocols</div>
                                </div>
                              </div>
                            </div>

                            {batch.protocols && batch.protocols.length > 0 && (
                              <div className="mb-3">
                                <h6 className="text-muted small mb-2">Protocols in Batch:</h6>
                                {batch.protocols.map((protocol) => (
                                  <div key={protocol.protocol_id} className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="small fw-medium">{protocol.protocol_name}</span>
                                    <div className="d-flex gap-2">
                                      <span className="badge bg-light text-dark">
                                        {protocol.completed_cases}/{protocol.total_cases} cases
                                      </span>
                                      <span className="badge bg-success">
                                        {protocol.completion_percentage}%
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="row">
                              <div className="col-6">
                                <div className="text-center">
                                  <div className="h6 text-success mb-1">
                                    {batch.protocols ? batch.protocols.reduce((sum, p) => sum + p.completed_cases, 0) : 0}
                                  </div>
                                  <div className="small text-muted">Completed Cases</div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="text-center">
                                  <div className="h6 text-warning mb-1">
                                    {batch.total_cases - (batch.protocols ? batch.protocols.reduce((sum, p) => sum + p.completed_cases, 0) : 0)}
                                  </div>
                                  <div className="small text-muted">Remaining Cases</div>
                                </div>
                              </div>
                            </div>

                            {batch.start_date && (
                              <div className="mt-3 pt-3 border-top">
                                <div className="d-flex justify-content-between small text-muted">
                                  <span>
                                    <i className="fas fa-calendar-alt me-1"></i>
                                    Started: {new Date(batch.start_date).toLocaleDateString()}
                                  </span>
                                  {batch.end_date && (
                                    <span>
                                      <i className="fas fa-calendar-times me-1"></i>
                                      Deadline: {new Date(batch.end_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                {batch.grace_period_hours && (
                                  <div className="text-center mt-2">
                                    <small className="text-muted">
                                      {batch.grace_period_hours}h grace period after deadline
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Protocol Details */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-list-alt text-primary me-2"></i>
                  Protocol Progress Details
                </h5>
              </div>
              <div className="card-body">
                {protocols.length > 0 ? (
                  <div className="row">
                    {protocols.map((protocol, index) => (
                      <div key={protocol.protocol_id} className="col-lg-6 col-md-12 mb-4">
                        <div className="card border h-100">
                          <div className="card-header bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 fw-bold">{protocol.protocol_name}</h6>
                              <span className={`badge bg-${getCompletionColor(protocol.completion_percentage)}`}>
                                {protocol.completion_percentage}%
                              </span>
                            </div>
                          </div>
                          <div className="card-body">
                            {protocol.protocol_description && (
                              <p className="text-muted small mb-3">{protocol.protocol_description}</p>
                            )}
                            
                            <div className="row mb-3">
                              <div className="col-6">
                                <div className="text-center">
                                  <div className="h5 text-primary mb-1">{protocol.total_cases}</div>
                                  <div className="small text-muted">Total Cases</div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="text-center">
                                  <div className="h5 text-info mb-1">{protocol.total_questions}</div>
                                  <div className="small text-muted">Questions per Case</div>
                                </div>
                              </div>
                            </div>

                            <div className="row mb-3">
                              <div className="col-4">
                                <div className="text-center">
                                  <div className="h6 text-success mb-1">{protocol.cases_completed}</div>
                                  <div className="small text-muted">Completed</div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="text-center">
                                  <div className="h6 text-warning mb-1">{protocol.cases_in_progress}</div>
                                  <div className="small text-muted">In Progress</div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="text-center">
                                  <div className="h6 text-secondary mb-1">
                                    {protocol.total_cases - protocol.cases_completed - protocol.cases_in_progress}
                                  </div>
                                  <div className="small text-muted">Not Started</div>
                                </div>
                              </div>
                            </div>

                            <div className="progress mb-2" style={{ height: '8px' }}>
                              <div 
                                className={`progress-bar bg-${getCompletionColor(protocol.completion_percentage)}`}
                                role="progressbar" 
                                style={{ width: `${protocol.completion_percentage}%` }}
                                aria-valuenow={protocol.completion_percentage} 
                                aria-valuemin="0" 
                                aria-valuemax="100"
                              ></div>
                            </div>
                            
                            <div className="d-flex justify-content-between small text-muted">
                              <span>
                                {protocol.answered_questions} of {protocol.total_questions} questions answered
                              </span>
                              <span>{protocol.completion_percentage}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Protocols Assigned</h5>
                    <p className="text-muted">You haven't been assigned any protocols yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Work History Section */}
        {work_history && work_history.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-history text-info me-2"></i>
                    Recent Work History (Last 30 Days)
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {work_history.slice(0, 14).map((day, index) => (
                      <div key={day.date} className="col-lg-3 col-md-4 col-sm-6 mb-3">
                        <div className="card border h-100">
                          <div className="card-body text-center">
                            <div className="text-primary mb-2">
                              <i className="fas fa-calendar-day"></i>
                            </div>
                            <h6 className="fw-bold">{new Date(day.date).toLocaleDateString()}</h6>
                            <div className="small text-muted">
                              <div><strong>{day.questions_answered}</strong> questions</div>
                              <div><strong>{day.protocols_worked_on}</strong> protocols</div>
                              <div><strong>{day.cases_worked_on}</strong> cases</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Completed Assignments Section */}
        {completed_assignments && completed_assignments.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    Completed Assignments ({completed_assignments.length})
                  </h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Protocol</th>
                          <th>Case ID</th>
                          <th>Questions Answered</th>
                          <th>Assigned Date</th>
                          <th>Completed Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completed_assignments.slice(0, 20).map((assignment, index) => (
                          <tr key={index}>
                            <td className="fw-bold">{assignment.protocol_name}</td>
                            <td>
                              <span className="badge bg-primary">{assignment.case_id}</span>
                            </td>
                            <td>
                              <span className="badge bg-success">{assignment.questions_answered}</span>
                            </td>
                            <td className="text-muted small">
                              {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="text-muted small">
                              {assignment.completed_at ? new Date(assignment.completed_at).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {completed_assignments.length > 20 && (
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Showing 20 of {completed_assignments.length} completed assignments
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inactive Questions Section */}
        {inactive_questions_summary && inactive_questions_summary.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-pause-circle text-warning me-2"></i>
                    Inactive Questions Assigned to You ({inactive_questions_summary.reduce((total, protocol) => total + protocol.inactive_questions_count, 0)})
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {inactive_questions_summary.map((protocol) => (
                      <div key={protocol.protocol_id} className="col-lg-6 col-md-12 mb-4">
                        <div className="card border h-100">
                          <div className="card-header bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 fw-bold">{protocol.protocol_name}</h6>
                              <span className="badge bg-warning">
                                {protocol.inactive_questions_count} inactive
                              </span>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="list-group list-group-flush">
                              {protocol.inactive_questions.slice(0, 10).map((question) => (
                                <div key={question.id} className="list-group-item px-0">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <p className="mb-1 fw-medium text-muted small">
                                        {question.text.length > 100
                                          ? `${question.text.substring(0, 100)}...`
                                          : question.text
                                        }
                                      </p>
                                      <div className="d-flex gap-2">
                                        <span className="badge bg-secondary badge-sm">{question.type}</span>
                                        {question.category && (
                                          <span className="badge bg-info badge-sm">{question.category}</span>
                                        )}
                                      </div>
                                    </div>
                                    <small className="text-muted">
                                      ID: {question.id}
                                    </small>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {protocol.inactive_questions.length > 10 && (
                              <div className="text-center mt-3">
                                <small className="text-muted">
                                  Showing 10 of {protocol.inactive_questions_count} inactive questions
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="alert alert-info mt-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Note:</strong> These questions were marked as inactive by program administrators.
                    They may have been answered before being deactivated and are shown here for your reference.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default DoctorProgressReport;